import {
  cloneElement,
  ReactElement,
  ReactNode,
  RefObject,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebouncedCallback } from 'use-debounce';

interface InlineFieldRenderProps<T> {
  value: T;
  onChange: (value: T) => void;
  onBlurWithin?: () => void;
  onBlur?: () => void;
  ref?: RefObject<any> | ((instance: any) => void);
}

export interface InlineFieldProps<T> {
  value: T;
  onChange: (value: T) => void;
  children:
    | ReactElement<InlineFieldRenderProps<T>>
    | ((props: InlineFieldRenderProps<T>) => ReactNode);
  ref?: RefObject<any>;
  changeOnDebounce?: boolean;
  /**
   * Hopefully temporary workaround for DateRangePicker, it can send broken ranges (we need to useDateRange() there so it doesn't happen)
   * @param value
   */
  tmp_canAcceptChange?: (value: T) => boolean;
  /**
   * If true, the component will not compare the value to the previous value and will always update the value.
   */
  doNotCompare?: boolean;
}

const noop = () => void 0;
/**
 * A component that wraps an input field and provides debounced updates to the parent component.
 * Generally it's discouraged to debounce onChangeCallback. (We should prefer debouncing the query that sources the data).
 * But this is created for existing use-cases where we need to debounce the input field.
 */
export function InlineField<T>({
  value,
  onChange,
  children,
  changeOnDebounce = true,
  tmp_canAcceptChange,
  doNotCompare = false
}: InlineFieldProps<T>) {
  const [internalValue, setInternalValue] = useState(value);
  const onChangeDebounced = useDebouncedCallback(
    changeOnDebounce ? onChange : noop,
    500
  );

  const lastValueOnChange = useRef(internalValue);
  const internalValueRef = useRef(internalValue);
  internalValueRef.current = internalValue;

  useLayoutEffect(() => {
    setInternalValue(value);
    lastValueOnChange.current = value;

    return () => {
      onChangeDebounced.cancel();
    };
  }, [value]);

  const handleChange = (value: T) => {
    if (tmp_canAcceptChange && !tmp_canAcceptChange(value)) {
      return;
    }
    if (doNotCompare || lastValueOnChange.current !== value) {
      internalValueRef.current = value;
      setInternalValue(value);
      onChangeDebounced(value);
    }
  };

  const ref = useHotkeys(
    'enter',
    () => {
      if (
        doNotCompare ||
        lastValueOnChange.current !== internalValueRef.current
      ) {
        onChangeDebounced.flush();
        onChange(internalValueRef.current);
      }
    },
    { enableOnFormTags: true }
  );

  const externalProps = {
    value: internalValue,
    onChange: handleChange,
    onBlurWithin: () => {
      if (
        doNotCompare ||
        lastValueOnChange.current !== internalValueRef.current
      ) {
        onChangeDebounced.cancel();
        onChange(internalValueRef.current);
      }
    },
    onBlur: () => {
      if (
        doNotCompare ||
        lastValueOnChange.current !== internalValueRef.current
      ) {
        onChangeDebounced.flush();
        onChange(internalValueRef.current);
      }
    },
    ref
  };

  return typeof children === 'function'
    ? children(externalProps)
    : cloneElement(children, externalProps);
}
